import { useEffect, useState } from "react";
import { Plus, Trash2, Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { name: "", location: "", rating: 5, text: "", service: "" };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.text) { toast.error("Name and review text are required."); return; }
    setSaving(true);
    try { await api.post("/testimonials", form); toast.success("Review added"); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.delete(`/testimonials/${id}`); setItems((i) => i.filter((x) => x.id !== id)); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-extrabold">Testimonials</h1><p className="text-muted-foreground">Customer reviews shown on the site.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary rounded-sm font-bold" data-testid="add-testimonial-btn"><Plus className="h-4 w-4 mr-1" /> Add Review</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Add Testimonial</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="testimonial-name" /></div>
                <div><Label className="font-semibold">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Watertown, WI" className="mt-1.5 rounded-sm" data-testid="testimonial-location" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Service</Label><Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Driveway" className="mt-1.5 rounded-sm" data-testid="testimonial-service" /></div>
                <div><Label className="font-semibold">Rating</Label>
                  <div className="flex gap-1 mt-3" data-testid="testimonial-rating">
                    {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}><Star className={`h-6 w-6 ${n <= form.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} /></button>)}
                  </div>
                </div>
              </div>
              <div><Label className="font-semibold">Review</Label><Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} className="mt-1.5 rounded-sm" data-testid="testimonial-text" /></div>
              <Button onClick={save} disabled={saving} className="w-full bg-primary rounded-sm font-bold" data-testid="testimonial-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Review"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-card border border-border p-5 relative group" data-testid={`testimonial-admin-${t.id}`}>
            <button onClick={() => remove(t.id)} className="absolute top-3 right-3 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
            <div className="flex gap-0.5">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
            <p className="text-sm mt-3 text-foreground/90">"{t.text}"</p>
            <div className="mt-3 pt-3 border-t border-border text-sm"><span className="font-bold">{t.name}</span> · {t.service} · {t.location}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
